import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackerAiClient as api } from '@/app/TrackerAi/api/trackerAiClient';
import { buildDateRangeQueryOptions } from '../utils/visibility-range';
import type { DomainFilterValue, VisibilityRange } from '../types';
import type {
  PromptHistoryItem,
  ProjectPromptListItem,
  ProjectVisibilitySummary,
  UserProjectInsight,
} from '../types';

export type ProjectPrompt = ProjectPromptListItem;

export interface ProjectData {
  insight: UserProjectInsight;
  visibilitySummary: ProjectVisibilitySummary;
  /** Empty unless the current page needs prompts (see `ProjectDataNeeds`). */
  prompts: ProjectPrompt[];
  /** Empty unless the current page needs execution history. */
  history: PromptHistoryItem[];
}

export interface ProjectDataFilters {
  topicFilterId: string | null;
  visibilityRange: VisibilityRange;
  customFrom: string;
  customTo: string;
  modelFilterId: string | null;
  /** Prompt rows only: the API narrows brands / metric columns to this domain. */
  domainFilterId: DomainFilterValue;
}

/** Range-based lists only some pages read; the rest skip those requests. */
export interface ProjectDataNeeds {
  prompts: boolean;
  history: boolean;
}

export interface RefreshOptions {
  /** When true, updates data without toggling the page loading skeleton. */
  silent?: boolean;
}

/** Keys each part was last loaded for (`null` = not loaded / stale). */
interface LoadedKeys {
  insight: string | null;
  summary: string | null;
  prompts: string | null;
  history: string | null;
}

const NOT_LOADED: LoadedKeys = { insight: null, summary: null, prompts: null, history: null };

export function useProjectData(
  projectId: string | null,
  filters: ProjectDataFilters,
  needs: ProjectDataNeeds,
) {
  const [data, setData] = useState<ProjectData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  /** Drops stale parallel fetches (e.g. refresh started before run finished). */
  const fetchGenerationRef = useRef(0);
  const nextFetchGeneration = () => ++fetchGenerationRef.current;
  /** Only the latest non-silent load may clear the page skeleton. */
  const loadingGenRef = useRef(0);
  const loadedRef = useRef<LoadedKeys>(NOT_LOADED);

  const rangeQuery = useMemo(
    () =>
      buildDateRangeQueryOptions(
        filters.visibilityRange,
        filters.customFrom,
        filters.customTo,
        {
          topicId: filters.topicFilterId ?? undefined,
          providerIds: filters.modelFilterId ? [filters.modelFilterId] : undefined,
        },
      ),
    [
      filters.topicFilterId,
      filters.visibilityRange,
      filters.customFrom,
      filters.customTo,
      filters.modelFilterId,
    ],
  );
  const rangeKey = useMemo(
    () => `${projectId ?? ''}|${JSON.stringify(rangeQuery)}`,
    [projectId, rangeQuery],
  );
  const promptsQuery = useMemo(
    () => ({ ...rangeQuery, domainFilter: filters.domainFilterId }),
    [rangeQuery, filters.domainFilterId],
  );
  const promptsKey = `${rangeKey}|${filters.domainFilterId}`;
  // Same render as the filter change, before the fetch effect. Otherwise the
  // previous summary paints once under the new dropdown / date range.
  const [pendingRangeKey, setPendingRangeKey] = useState(rangeKey);
  if (projectId && pendingRangeKey !== rangeKey) {
    setPendingRangeKey(rangeKey);
    setLoading(true);
    setError('');
  }
  // Prompts and history stay [] until their tab asks for them. Set loading in
  // this render so the tab does not paint its empty state before the effect.
  const [pendingPromptsKey, setPendingPromptsKey] = useState<string | null>(null);
  if (projectId && needs.prompts && pendingPromptsKey !== promptsKey) {
    setPendingPromptsKey(promptsKey);
    setLoading(true);
    setError('');
  }
  const [pendingHistoryKey, setPendingHistoryKey] = useState<string | null>(null);
  if (projectId && needs.history && pendingHistoryKey !== rangeKey) {
    setPendingHistoryKey(rangeKey);
    setLoading(true);
    setError('');
  }

  const load = useCallback(
    async (options?: RefreshOptions & { force?: boolean }) => {
      if (!projectId) {
        loadedRef.current = NOT_LOADED;
        setData(null);
        setLoadFailed(false);
        return null;
      }
      const force = options?.force === true;
      const loaded = loadedRef.current;
      const want = {
        insight: force || loaded.insight !== projectId,
        summary: force || loaded.summary !== rangeKey,
        prompts: needs.prompts && (force || loaded.prompts !== promptsKey),
        history: needs.history && (force || loaded.history !== rangeKey),
      };
      if (!want.insight && !want.summary && !want.prompts && !want.history) {
        return null;
      }

      const silent = options?.silent === true;
      const generation = nextFetchGeneration();
      const loadingGen = silent ? 0 : ++loadingGenRef.current;
      if (!silent) {
        setLoading(true);
        setError('');
      }
      try {
        const [insight, visibilitySummary, promptItems, history] = await Promise.all([
          want.insight
            ? (api.getProjectInsight(projectId) as Promise<UserProjectInsight>)
            : null,
          want.summary
            ? (api.getProjectVisibilitySummary(
                projectId,
                rangeQuery,
              ) as Promise<ProjectVisibilitySummary>)
            : null,
          want.prompts
            ? api.getProjectPrompts(projectId, promptsQuery)
            : null,
          want.history
            ? api.getProjectExecutions(projectId, rangeQuery)
            : null,
        ]);
        if (generation !== fetchGenerationRef.current) return null;

        const keepPrompts = !want.prompts && loaded.prompts === promptsKey;
        const keepHistory = !want.history && loaded.history === rangeKey;
        loadedRef.current = {
          insight: projectId,
          summary: rangeKey,
          prompts: want.prompts || keepPrompts ? promptsKey : null,
          history: want.history || keepHistory ? rangeKey : null,
        };

        setData((prev) => {
          const nextInsight = insight ?? prev?.insight;
          const nextSummary = visibilitySummary ?? prev?.visibilitySummary;
          if (!nextInsight || !nextSummary) return prev;
          return {
            insight: nextInsight,
            visibilitySummary: nextSummary,
            prompts: promptItems
              ? promptItems.rows
              : keepPrompts && prev
                ? prev.prompts
                : [],
            history: history?.rows ?? (keepHistory && prev ? prev.history : []),
          };
        });
        setLoadFailed(false);
        return null;
      } catch {
        if (!silent) setLoadFailed(true);
        return null;
      } finally {
        if (!silent && loadingGen === loadingGenRef.current) setLoading(false);
      }
    },
    [projectId, rangeKey, rangeQuery, promptsKey, promptsQuery, needs.prompts, needs.history],
  );

  /** Reloads every part the current page uses. */
  const refresh = useCallback(
    (options?: RefreshOptions) => load({ ...options, force: true }),
    [load],
  );

  const fetchPrompts = useCallback(async () => {
    if (!projectId) return null;
    if (!needs.prompts) {
      // Not shown on this page — reload when a page that lists prompts mounts.
      loadedRef.current = { ...loadedRef.current, prompts: null };
      return null;
    }
    const generation = nextFetchGeneration();
    const promptsResult = await api.getProjectPrompts(projectId, promptsQuery);
    const prompts = promptsResult.rows;
    return { prompts, current: generation === fetchGenerationRef.current };
  }, [projectId, needs.prompts, promptsQuery]);

  /** Reloads prompt list rows only — no page skeleton. Used after each runPrompt. */
  const refreshPromptsSilent = useCallback(async () => {
    try {
      const fetched = await fetchPrompts();
      if (!fetched) return null;
      if (!fetched.current) return fetched.prompts;
      loadedRef.current = { ...loadedRef.current, prompts: promptsKey };
      setData((prev) => (prev ? { ...prev, prompts: fetched.prompts } : prev));
      return fetched.prompts;
    } catch {
      return null;
    }
  }, [fetchPrompts, promptsKey]);

  /**
   * Patches a single prompt row from the latest list payload.
   * Falls back to a full silent prompts refresh when the row is missing.
   */
  const refreshPromptRow = useCallback(
    async (promptId: string) => {
      try {
        const fetched = await fetchPrompts();
        if (!fetched) return null;
        const { prompts } = fetched;
        const updated = prompts.find((p) => p.id === promptId);
        if (!fetched.current) {
          return updated ?? null;
        }
        if (!updated) {
          setData((prev) => (prev ? { ...prev, prompts } : prev));
          return null;
        }
        setData((prev) => {
          if (!prev) return prev;
          const exists = prev.prompts.some((p) => p.id === promptId);
          return {
            ...prev,
            prompts: exists
              ? prev.prompts.map((p) => (p.id === promptId ? updated : p))
              : [updated, ...prev.prompts],
          };
        });
        return updated;
      } catch {
        return null;
      }
    },
    [fetchPrompts],
  );

  useEffect(() => {
    void load();
  }, [load]);

  return {
    data,
    loading,
    error,
    setError,
    loadFailed,
    refresh,
    refreshPromptsSilent,
    refreshPromptRow,
  };
}
